import React from 'react';
import { Avatar } from '@mui/material';
import {
  getMembershipTierChrome,
  type MembershipTierId,
} from './membershipTierChrome';
import './MembershipTierChrome.css';

type Size = 'sm' | 'md' | 'lg';

const SIZE_PX: Record<Size, number> = {
  sm: 28,
  md: 40,
  lg: 104,
};

type Props = {
  name?: string | null;
  src?: string | null;
  tier?: MembershipTierId | string | null;
  /** Only draw the ring when membership is live */
  showRing?: boolean;
  size?: Size;
  className?: string;
  sx?: Record<string, unknown>;
};

/** Profile avatar with animated Bronze / Silver / Gold membership orbit ring. */
export const MembershipAvatar: React.FC<Props> = ({
  name,
  src,
  tier,
  showRing = false,
  size = 'sm',
  className = '',
  sx = {},
}) => {
  const chrome = showRing ? getMembershipTierChrome(tier) : null;
  const px = SIZE_PX[size];
  const avatar = (
    <Avatar
      className={className}
      src={src || undefined}
      sx={{
        width: px,
        height: px,
        bgcolor: '#CC1B1B',
        color: '#ffffff',
        fontSize: size === 'lg' ? '2rem' : size === 'md' ? '1rem' : '0.75rem',
        fontWeight: 700,
        ...sx,
      }}
    >
      {name?.slice(0, 1).toUpperCase() || 'U'}
    </Avatar>
  );

  if (!chrome) return avatar;

  const ringStyle = {
    ['--mt-ring-from' as string]: chrome.ringFrom,
    ['--mt-ring-via' as string]: chrome.ringVia,
    ['--mt-ring-to' as string]: chrome.ringTo,
    ['--mt-glow' as string]: chrome.glow,
    ['--mt-track' as string]: chrome.track,
    ['--mt-spin-duration' as string]: '12s',
  };

  return (
    <span
      className={`mt-avatar-ring mt-avatar-ring--${size} mt-avatar-ring--${chrome.id}`}
      style={ringStyle}
      title={`${chrome.label} member`}
      aria-label={`${chrome.label} member`}
    >
      {/* Orbiting conic sweep — rotates slowly around the avatar */}
      <span className="mt-avatar-ring__orbit" aria-hidden="true" />
      {/* Soft static base track so the circle always reads the tier color */}
      <span className="mt-avatar-ring__track" aria-hidden="true" />
      <span className="mt-avatar-ring__inner">{avatar}</span>
    </span>
  );
};

export default MembershipAvatar;
